<template>
  <div>
    <!-- ⚠ confirmLabel="Save", not the default "Done". Closing this selector IS the save -
         onConfiguringChange posts on close - so the quick view must not offer a word that reads
         like dismissing a dialog.
         ⚠ hideColourAndTransmission: buildPayload posts the description alone, so a colour or
         transmission picked here would be dropped without a word. Not offered rather than lost.
         ⚠ No inventoryType: the selector wants the definedTypes object, not the record's string type (that
         raised a prop warning). Its only reader, the AutoCheck control in the selector's modal, uses it only
         alongside purchasedFromType/newInventory, which this host does not pass - pass the object if either is added. -->
    <make-model-selector
      v-model="selectedMakeModel"
      :hideTrigger="true"
      :useValidator="$validator"
      :forceColorSelection="false"
      :forceTransmissionSelection="false"
      :hideColourAndTransmission="true"
      :allowRestore="false"
      confirmLabel="Save"
      :vinNo="inventory.vinNo"
      :inventoryId="inventory.id"
      :inventoryNotes="inventory.notes"
      :carfaxRating="inventory.carFaxRating"
      :vinDecodeName="inventory.vinDecodeName"
      :vinDecodeDetails="inventory.vinDecodeDetails"
      :showCarfaxComparison="showComparison"
      v-on:configuring-change="onConfiguringChange"
      v-on:carfax-apply="onCarfaxApply"
      v-on:carfax-dismiss="onCarfaxDismiss"
      ref="makeModelSelectorRef">
    </make-model-selector>
  </div>
</template>

<script>
/*
 * Correct a vehicle's model WITHOUT opening the vehicle-edit screen.
 *
 * Meeting change #10, 23 Sep. From the inventory quick view the only route to the model selector was
 * Edit, which navigated to the whole edit screen - and that screen's Save posts ~70 fields and the
 * entire attribute array, so a model correction rewrote every screen-derived value on the record.
 * Garrett: "open the modal without editing the vehicle, because then you have other problems that
 * you're saving." / "When I click Edit, I don't think you should be editing the vehicle, I think you
 * should be editing the model selector."
 *
 * ⚠ useModal stays TRUE and the selector's OWN modal is the isolated modal. Passing useModal false
 * would render the inline panel AND make the selector emit `parent-make-model-onSave` on the shared
 * eventBus (MakeModelSelector.vue:2269) - which the inventory mixin binds to markMakeModelReconciled
 * for every mounted inventory-mixin screen (mixins/inventory/index.js:2282). One correction here
 * would reconcile unrelated records elsewhere on the page.
 *
 * ⚠ No inventory mixin, deliberately. This mirrors ApprovePpaAction, the only other host carrying
 * none, and it is why every handler bound above is defined HERE. Binding carfax-dismiss to a
 * mixin-only method is a defect this codebase has already shipped once (ApprovePpaAction.vue:55).
 *
 * ⚠ It saves through the narrow endpoint, not /inventory/update. That is the entire point: an
 * unrelated pending edit cannot be swept in because it is never sent.
 */
import { createNamespacedHelpers } from 'vuex'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import utilitiesMixin from '@/mixins/generic/utilities'
import MakeModelSelector from '@/components/generic/MakeModelSelector'
import inventoryService from '@/services/inventoryService'
import { setInventoryAttribute } from '@/utils/inventory_attributes'
import { readMakeModelFields, buildCarfaxOverwriteNote, buildCarfaxModelMappingRecord } from '@/utils/carfax_make_model_resolution'

const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters

const DESCRIPTION_ATTRIBUTES = ['Trim', 'Style', 'ProductionYear', 'Make', 'Model']

export default {
  name: 'CarfaxModelCorrectionModal',
  mixins: [validateBeforeSubmitMixin, utilitiesMixin],
  /*
   * ⚠ Registered HERE. The tag is normally supplied by the inventory mixin, which registers it for
   * every component that mixes it in - and this one deliberately does not. Without this the tag is
   * simply unknown and the modal renders empty, which shallowMount cannot catch because it stubs
   * unknown components silently. Found in the browser, not by the specs.
   */
  components: {
    'make-model-selector': MakeModelSelector
  },
  props: {
    inventory: {
      type: Object,
      required: true
    },
    /*
     * ⚠ Opened by a PROP, not by the host calling a method on a $ref.
     *
     * This component renders through the global modal portal so it is not clamped to the quick view's
     * panel width - and portalled content is no longer reachable as a $ref on the host, so a
     * ref.open() call silently did nothing and the host fell through to navigating to the edit
     * screen. A prop crosses the portal; a ref does not.
     */
    open: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    open: function (isOpen) {
      if (isOpen) this.raise()
    }
  },
  data: function () {
    return {
      selectedMakeModel: {},
      //Held until the save, on the same latch the edit screen uses, so closing without a correction
      //writes nothing.
      carfaxOverwriteNote: '',
      carfaxModelMappingRecord: null,
      wasOpen: false,
      saving: false,
      /*
       * ⚠ Host-controlled, never hard-coded true. showInlineCarfaxComparison is
       * `showCarfaxComparison && useModal && isConfiguring`, and the selector's MAIN footer - the one
       * holding Done - renders only when that is false. Pinned true, the operator applies a model and
       * then has no way to commit it.
       */
      showComparison: false,
      //Nothing was corrected, so nothing should be written. Cancel must not produce a save.
      applied: false,
      /*
       * What the description looked like when the selector opened, so closing can tell a correction
       * from a Cancel without relying on `applied` alone.
       *
       * ⚠ `applied` ONLY EVER MEANT "the CARFAX prompt was applied". It is set in one place, from the
       * selector's carfax-apply event, and that event fires from the inline MODEL choice alone. The
       * other reconcile path - year, trim and style - ends in carfax-dismiss after arming the
       * pop-fill chips, and a hand-typed trim sets nothing at all. So every correction that was not a
       * model apply closed silently and wrote nothing. Measured in the browser on FP6111, whose model
       * already matched: Reconcile, then the footer button, produced no request whatsoever.
       */
      fingerprintOnOpen: null
    }
  },
  computed: {
    ...mapVehicleMakeGetters(['allVehicleMakes'])
  },
  methods: {
    attributeValue: function (name) {
      const attributes = (this.inventory && this.inventory.attributes) || []
      const found = attributes.find((x) => x.name === name && x.status !== 'Deleted')
      return found ? found.value : ''
    },
    /*
     * The description as this modal would POST it.
     *
     * ⚠ Exactly the fields buildPayload carries, and deliberately no more. The selector also holds
     * colours and transmission; those are not sent, so treating a change to one of them as a
     * correction would write a reconciled flag and a note for something the save then drops.
     *
     * ⚠ Everything absent normalises to the same empty string. The selector rewrites null to '' and
     * back as the operator moves through it, and without this a modal opened and closed untouched can
     * look changed - which would write on Cancel, the one thing that must never happen.
     *
     * ⚠ customMakeModel is NOT in here, and it was. It is a DERIVED display string - the selector
     * rebuilds it as "year make model trim style" from updateCustomMakeModel, which every one of its
     * fourteen onSelected, added and fill handlers calls, the two colour pickers included. So picking only a
     * colour rewrote it, the fingerprint moved, and Save posted a reconciled flag and a rewritten
     * description for a change the payload then dropped - and told the operator "Vehicle model
     * updated". Every real change it reflects already moves one of the seven fields below.
     */
    correctionFingerprint: function () {
      const values = this.selectedMakeModel || {}
      const flat = (value) => (value === null || value === undefined ? '' : String(value).trim())
      return [
        values.makeId, values.modelId, values.productionYear, values.trim, values.style,
        values.customMakeName, values.customModelName
      ].map(flat).join('|')
    },
    /*
     * Seed the selector from the record, then hand it the CARFAX rating so it raises the reconcile
     * prompt itself - the same call EditInventory makes on arrival (EditInventory.vue:2617).
     */
    raise: function () {
      const make = this.allVehicleMakes.find((x) => x.id === this.inventory.vehicleMakeId) || null
      const model = make && make.versions
        ? make.versions.find((x) => x.id === this.inventory.vehicleMakeVersionId) || null
        : null

      this.selectedMakeModel = {
        makeId: this.inventory.vehicleMakeId,
        selectedMake: make,
        modelId: this.inventory.vehicleMakeVersionId,
        selectedModel: model,
        productionYear: this.inventory.vehicleProductionYear,
        trim: this.attributeValue('Trim'),
        style: this.attributeValue('Style'),
        customMakeName: this.attributeValue('Make') || null,
        customModelName: this.attributeValue('Model') || null,
        customMakeModel: this.inventory.customVehicleMakeVersion,
        exteriorColor: this.attributeValue('ExteriorColor'),
        interiorColor: this.attributeValue('InteriorColor'),
        transmissionType: this.inventory.transmissionType
      }
      this.carfaxOverwriteNote = ''
      this.carfaxModelMappingRecord = null
      this.showComparison = true
      this.applied = false
      this.fingerprintOnOpen = this.correctionFingerprint()

      this.$nextTick(() => {
        const selector = this.$refs.makeModelSelectorRef
        if (!selector) return
        /*
         * ⚠ openForCarfaxComparison, not offerCarfaxMakeModel. The latter arms the pop-fill chips and
         * opens the selector on its ordinary form; only this one marks the selector as opened FOR the
         * comparison, which is what makes its Cancel close the whole thing rather than strand the
         * operator in a make/model form they never asked for. Its own comment describes this exact
         * case - a caller that opens the selector for no other purpose than to host the comparison.
         */
        selector.openForCarfaxComparison()
        //The chips still get armed, so the remaining CARFAX values are offered once a model is chosen.
        selector.offerCarfaxMakeModel(this.inventory.carFaxRating)
      })
    },
    /*
     * The operator ticked a row and pressed Apply. Same shape as the edit screen's handler: snapshot
     * BEFORE the apply, because applyCarfaxMakeModel opens by nulling the year, model, trim and style
     * and selectedMakeModel is the selector's own object rather than a copy of it.
     */
    /*
     * ⚠ The payload is the chosen MODEL, not the chosen row. The selector already unwraps it -
     * `$emit('carfax-apply', chosenRow && chosenRow.model)` - so unwrapping again yields undefined,
     * and the mapping record then silently refuses to build because it has no catalogue id. The
     * reconcile component's own `apply` event DOES emit the row, which is what makes this easy to
     * get wrong; the inventory mixin sits on that one, this sits on the selector's.
     */
    onCarfaxApply: function (chosenModel) {
      const selector = this.$refs.makeModelSelectorRef
      if (!selector) return

      /*
       * Captured synchronously, all three, because each is about to become unreadable: `before`
       * because applyCarfaxMakeModel opens by nulling those fields, and the comparison and its flags
       * because clearing showComparison below tears them down.
       */
      const before = readMakeModelFields(this.selectedMakeModel)
      const carfaxMakeModel = (selector.carfaxComparisonForDisplay || {}).carfaxMakeModel
      const differences = selector.inlineCarfaxDifferences

      selector.applyCarfaxMakeModel(carfaxMakeModel, chosenModel)

      /*
       * ⚠ nextTick, as ApprovePpaAction's equivalent also does. The applied values reach this
       * component through v-model, which lands on the NEXT tick - read synchronously, `after` still
       * equals `before`, so the note builder decides nothing moved and returns an empty string. The
       * note is the only durable record of what the correction replaced, so losing it is silent.
       */
      this.$nextTick(() => {
        const after = readMakeModelFields(this.selectedMakeModel)

        this.carfaxOverwriteNote = buildCarfaxOverwriteNote(before, after, differences, this.inventory.name)
        /*
         * ⚠ Keyed on the model the record ENDED UP ON, not on chosenModel. Apply with nothing ticked
         * applies the "current" row, whose model is null by design, and the selector falls back to
         * CARFAX's own resolution - which still lands a catalogue model. Keyed on the ticked row,
         * that whole path taught the mapping nothing, and it is the common path: the prompt only
         * pre-ticks where a mapping already exists, so a first sighting never has a tick to read.
         */
        this.carfaxModelMappingRecord = buildCarfaxModelMappingRecord(
          carfaxMakeModel, this.selectedMakeModel && this.selectedMakeModel.modelId, after.trim)

        this.applied = true
        //Hands the form back: the comparison has done its job and the main footer holds Done.
        this.showComparison = false
      })
    },
    onCarfaxDismiss: function () {
      //Defined here rather than inherited: this component carries no inventory mixin, and binding
      //this to a mixin-only method is how the prompt once became impossible to close.
      this.showComparison = false
    },
    /*
     * The selector's modal has no Cancel - canCancelDialog defaults false, so Done is the only exit.
     * Closing therefore means the operator finished, and that is when this posts.
     */
    onConfiguringChange: function (isOpen) {
      if (isOpen) {
        this.wasOpen = true
        return
      }
      if (!this.wasOpen) return
      this.wasOpen = false
      //Let the host drop its flag either way, or the modal can never be raised a second time.
      this.$emit('closed')
      /*
       * Closing having changed nothing is a Cancel, and a Cancel must not write.
       *
       * ⚠ `applied` is kept as an OR rather than replaced. Applying CARFAX values that already match
       * moves no field, but it is still the operator saying the record is reconciled - and the
       * reconciled flag is the point of that gesture.
       */
      const baseline = this.fingerprintOnOpen
      this.fingerprintOnOpen = null

      /*
       * ⚠ null means this component never raised the selector, so there is no baseline and nothing
       * the operator can have corrected HERE. The old gate got that for free, because `applied`
       * starts false; a fingerprint comparison has to say it out loud.
       */
      if (!this.applied && baseline === null) return
      if (!this.applied && this.correctionFingerprint() === baseline) return
      this.save()
    },
    buildPayload: function () {
      const payload = {
        inventoryId: this.inventory.id,
        /*
         * ⚠ The record's rowversion, so the server can refuse this save if the vehicle moved on
         * while the modal was open. Absent or stale is the difference between a save that lands and
         * one that is refused - it is not decoration.
         */
        expectedVersion: this.inventory.version,
        vehicleMakeId: this.selectedMakeModel.makeId < 1 ? null : this.selectedMakeModel.makeId,
        vehicleMakeVersionId: this.selectedMakeModel.modelId < 1 ? null : this.selectedMakeModel.modelId,
        customVehicleMakeVersion: this.selectedMakeModel.customMakeModel,
        makeModelReconciled: true,
        carfaxOverwriteNote: this.carfaxOverwriteNote,
        carfaxModelMapping: this.carfaxModelMappingRecord,
        /*
         * ⚠ Seeded from the record's OWN rows so each carries its id. Without the id the server adds
         * a second row of the same name rather than updating the one that is there - the duplicate
         * the attribute helpers exist to prevent.
         */
        attributes: ((this.inventory && this.inventory.attributes) || [])
          .filter((x) => DESCRIPTION_ATTRIBUTES.indexOf(x.name) > -1)
          .map((x) => ({ id: x.id, name: x.name, value: x.value, status: x.status }))
      }

      setInventoryAttribute(payload, 'Style', this.selectedMakeModel.style)
      setInventoryAttribute(payload, 'Trim', this.selectedMakeModel.trim)
      setInventoryAttribute(payload, 'ProductionYear', this.selectedMakeModel.productionYear)
      setInventoryAttribute(payload, 'Make', this.selectedMakeModel.customMakeName)
      setInventoryAttribute(payload, 'Model', this.selectedMakeModel.customModelName)

      return payload
    },
    save: function () {
      if (this.saving) return
      this.saving = true

      inventoryService.updateVehicleMakeModel(this.buildPayload())
        .then((response) => {
          this.saving = false
          //⚠ A refusal is a 200 carrying { message, errors } and no record - it is not a save.
          if (!response || response.errors || !response.inventory) {
            this.failedToast((response && response.message) || 'The vehicle model could not be updated')
            this.$emit('failed', response)
            return
          }
          this.successToast('Vehicle model updated')
          /*
           * ⚠ The whole updated record travels back and the host MUST merge it, or this screen keeps
           * the pre-save rowversion and the server refuses its next save as a conflict that never
           * happened.
           *
           * ⚠ This once claimed the stale copy would produce a 500. It would not have. InventoryModel
           * carried no version at all then, so every save path re-loaded the record inside the request
           * and compared a token against itself - the check always passed and the second writer
           * silently won. The version now round-trips, which is what makes the merge load-bearing.
           */
          this.$emit('saved', response.inventory)
        })
        .catch((error) => {
          this.saving = false
          this.failedToast((error && error.message) || 'The vehicle model could not be updated')
          this.$emit('failed', error)
        })
    }
  }
}
</script>
